import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';

interface NotificationItem {
  readonly id: string;
  readonly alertId: string;
  readonly status: 'PENDING' | 'SENT' | 'READ' | 'FAILED';
  readonly createdAt: string;
  readonly readAt: string | null;
  readonly alertPriority: string;
  readonly alertSubtype: string;
  readonly alertTitleKey: string | null;
  readonly alertTitleParams: Record<string, unknown> | null;
  readonly alertTitleText: string | null;
  readonly alertEntityType: string;
}

function titleFor(t: (k: string, o?: Record<string, unknown>) => string, item: NotificationItem): string {
  if (item.alertTitleText) return item.alertTitleText;
  if (item.alertTitleKey) return t(item.alertTitleKey, item.alertTitleParams ?? {});
  return item.alertSubtype;
}

/** 三段分组（S18："今天 / 本周 / 更早"），按创建时间的自然日/自然周划界。 */
function bucketFor(createdAt: string): 'today' | 'week' | 'earlier' {
  const now = new Date();
  const at = new Date(createdAt);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (at >= startOfToday) return 'today';
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  if (at >= startOfWeek) return 'week';
  return 'earlier';
}

/**
 * 通知铃铛（S18）。未读数每 30s 轮询一次；下拉面板按时间分组，点击一条
 * 既标记已读又跳转到预警中心并直接打开对应详情（`?open=<alertId>`，见
 * AlertCenterPage 对该查询参数的处理）。V1 仅 in-app 渠道（§39）——
 * 这个铃铛本身就是唯一的"投递"动作，没有额外的推送步骤。
 */
export function NotificationBell(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });

  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiFetch<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });

  const { data: list } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => apiFetch<{ items: NotificationItem[] }>('/notifications?limit=20'),
    enabled: open,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiFetch(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const markAllReadMutation = useMutation({
    mutationFn: () => apiFetch('/notifications/read-all', { method: 'POST' }),
    onSuccess: invalidate,
  });

  const openNotification = (n: NotificationItem): void => {
    if (n.status !== 'READ') markReadMutation.mutate(n.id);
    setOpen(false);
    navigate(`/alerts?open=${n.alertId}`);
  };

  const groups: Array<{ key: 'today' | 'week' | 'earlier'; labelKey: string; items: NotificationItem[] }> = [
    { key: 'today', labelKey: 'notifications.groupToday', items: [] },
    { key: 'week', labelKey: 'notifications.groupWeek', items: [] },
    { key: 'earlier', labelKey: 'notifications.groupEarlier', items: [] },
  ];
  for (const item of list?.items ?? []) {
    groups.find((g) => g.key === bucketFor(item.createdAt))!.items.push(item);
  }

  const count = unread?.count ?? 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        aria-label={t('notifications.bellLabel')}
        onClick={() => setOpen((o) => !o)}
      >
        🔔{count > 0 && <span className="badge badge--danger" style={{ marginLeft: 4 }}>{count}</span>}
      </button>

      {open && (
        <>
          <div
            role="presentation"
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 40 }}
          />
          <div
            className="card"
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 'var(--space-2)',
              width: '360px',
              maxHeight: '480px',
              overflow: 'auto',
              padding: 'var(--space-3)',
              zIndex: 41,
            }}
          >
            <div className="row row--between">
              <strong>{t('notifications.title')}</strong>
              {count > 0 && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => markAllReadMutation.mutate()}
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
            {(list?.items.length ?? 0) === 0 ? (
              <p className="field-hint">{t('notifications.empty')}</p>
            ) : (
              groups.map(
                (g) =>
                  g.items.length > 0 && (
                    <div key={g.key} style={{ marginTop: 'var(--space-2)' }}>
                      <div className="field-hint">{t(g.labelKey)}</div>
                      {g.items.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => openNotification(n)}
                          className="data-row"
                          style={{
                            gap: 'var(--space-2)',
                            alignItems: 'flex-start',
                          }}
                        >
                          {n.status !== 'READ' && (
                            <span
                              aria-hidden="true"
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: 'var(--color-danger)',
                                marginTop: 6,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <span style={{ flex: 1 }}>
                            <div>{titleFor(t, n)}</div>
                            <div className="field-hint">{fmt.format(new Date(n.createdAt))}</div>
                          </span>
                        </button>
                      ))}
                    </div>
                  ),
              )
            )}
            <div className="row row--between" style={{ marginTop: 'var(--space-3)' }}>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setOpen(false);
                  navigate('/alerts');
                }}
              >
                {t('notifications.viewAllAlerts')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
